import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, Download } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const usd = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e6) return `$${(n / 1e6).toFixed(1)}m`;
  return `$${Math.round(n || 0).toLocaleString()}`;
};
const int = (n) => (n || n === 0 ? Math.round(n).toLocaleString() : "—");
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const COLUMNS = [
  ["county", "County", (r) => r.county],
  ["players", "Players", (r) => int(r.players)],
  ["land_acres", "Land, acres", (r) => r.land_acres],
  ["capex_usd", "Capex", (r) => usd(r.capex_usd)],
  ["opex_usd", "Opex a year", (r) => usd(r.opex_usd)],
  ["annual_revenue_usd", "Revenue a year", (r) => usd(r.annual_revenue_usd)],
  ["brazil_shortfall", "Short of Brazil", (r) => int(r.brazil_shortfall)],
  ["brazil_closing_capex_usd", "Cost to close", (r) => usd(r.brazil_closing_capex_usd)],
  ["brazil_share_pct", "Share of Brazil", (r) => `${r.brazil_share_pct}%`],
];

const Totals = ({ t, testId }) => (
  <div data-testid={testId}>
    <p className="mono text-[0.58rem] uppercase tracking-[0.2em] text-ochre">{t.label} · {t.counties} counties</p>
    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">{t.note}</p>
    <div className="mt-6 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4">
      {[["Players reached", int(t.players)],
        ["Land needed", `${int(t.land_acres)} acres`],
        ["Capex", usd(t.capex_usd)],
        ["Opex a year", usd(t.opex_usd)],
        ["Revenue a year", usd(t.annual_revenue_usd)],
        ["Operating surplus a year", usd(t.operating_surplus_usd)],
        ["Payback period", t.payback_years ? `${t.payback_years} years` : "Needs blended funding"],
        ["Value per dollar", `${t.value_per_dollar}`],
        ["Short of Brazil", int(t.brazil_shortfall)],
        ["Cost to close the Brazil gap", usd(t.brazil_closing_capex_usd)]].map(([k, v]) => (
        <div key={k} className="bg-[#0b0b0b] p-6">
          <p className="mono text-[0.58rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
          <p className="mono mt-3 text-sm text-white">{v}</p>
        </div>
      ))}
    </div>
  </div>
);

/** Every county totalled in one view: the 18 strategic counties, all 47, and the Brazil gap order. */
export default function NationalRollup() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [scope, setScope] = useState("priority");
  const [sort, setSort] = useState("brazil_shortfall");
  const [asc, setAsc] = useState(false);

  useSeo({
    title: "The National Rollup — KickStartKenyaFootball",
    description:
      "Every Kenyan county's proposed football programme totalled in one view: players reached, land needed, capex, opex and potential revenue, for the 18 strategic counties and for all 47, ranked by how far behind Brazil's density each county still is.",
  });

  useEffect(() => {
    api.get("/national-rollup").then((r) => setD(r.data))
      .catch(() => setErr("We could not load the rollup. Refresh to try again."));
  }, []);

  const rows = useMemo(() => {
    if (!d) return [];
    const base = scope === "priority" ? d.rows.filter((r) => r.priority) : d.rows;
    const dir = asc ? -1 : 1;
    return [...base].sort((x, y) =>
      dir * (sort === "county" ? x.county.localeCompare(y.county) : (y[sort] || 0) - (x[sort] || 0)));
  }, [d, scope, sort, asc]);

  const clickSort = (key) => {
    if (key === sort) return setAsc((v) => !v);
    setSort(key);
    setAsc(false);
  };

  if (err)
    return (
      <div className="bg-black px-5 py-24 text-center" data-testid="rollup-error">
        <h1 className="display-3">{err}</h1>
        <button type="button" onClick={() => window.location.reload()} className="btn-primary mt-8">Retry</button>
      </div>
    );
  if (!d)
    return (
      <div className="bg-black px-5 py-24 text-center" data-testid="rollup-loading">
        <p className="mono text-[0.6rem] uppercase tracking-[0.2em] text-ochre">Totalling all 47 counties…</p>
      </div>
    );

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="national-rollup">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <Reveal>
          <p className="eyebrow">One view, every county</p>
          <h1 className="display-1 mt-5 max-w-4xl">{d.heading}</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/national-rollup.pdf`}
            className="btn-primary mt-7 inline-flex items-center gap-2" data-testid="rollup-pdf">
            <Download size={14} /> Download the whole rollup as a PDF
          </a>
        </Reveal>

        <div className="mt-14"><Totals t={d.priority_totals} testId="rollup-priority-totals" /></div>
        <div className="mt-16"><Totals t={d.national_totals} testId="rollup-national-totals" /></div>

        <section className="mt-16 scroll-mt-28" data-testid="rollup-table-section">
          <p className="eyebrow">County by county</p>
          <h2 className="display-3 mt-4">The Whole Programme, Line By Line</h2>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-zinc-400">{d.brazil_note}</p>

          <div className="mt-7 flex flex-wrap gap-3">
            {[["priority", "The 18 strategic counties"], ["all", "All 47 counties"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setScope(id)} data-testid={`rollup-scope-${id}`}
                className={`mono border px-4 py-2.5 text-[0.6rem] uppercase tracking-[0.16em] transition-colors ${
                  scope === id ? "border-ochre text-ochre" : "border-zinc-700 text-zinc-400 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>

          <div className="mt-6 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[900px] text-left" data-testid="rollup-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {COLUMNS.map(([key, label]) => (
                    <th key={key} className="px-4 py-3">
                      <button type="button" onClick={() => clickSort(key)} data-testid={`rollup-sort-${key}`}
                        className={`mono inline-flex items-center gap-1.5 text-[0.58rem] uppercase tracking-[0.16em] transition-colors ${
                          sort === key ? "text-ochre" : "text-zinc-300 hover:text-white"}`}>
                        {label} <ArrowUpDown size={11} />
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.county} className="border-b border-zinc-900" data-testid={`rollup-row-${slugify(r.county)}`}>
                    <td className="px-4 py-4">
                      <Link to={`/counties/${slugify(r.county)}/one-page`} className="font-display text-sm uppercase text-white hover:text-ochre">
                        {r.county}
                      </Link>
                      {r.priority ? (
                        <span className="mono ml-2 text-[0.55rem] uppercase text-ochre" aria-label="One of the 18 strategic counties">
                          {" "}P18
                        </span>
                      ) : null}
                    </td>
                    {COLUMNS.slice(1).map(([key, , read]) => (
                      <td key={key} className={`mono px-4 py-4 text-xs ${key === "brazil_shortfall" ? "text-maasai" : "text-zinc-300"}`}>
                        {read(r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-5 max-w-3xl text-xs leading-relaxed text-zinc-500">{d.note}</p>
          <div className="mono mt-6 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
            <Link to="/counties" className="text-ochre hover:text-white" data-testid="rollup-to-map">County map</Link>
            <Link to="/counties/compare-sheet" className="text-ochre hover:text-white" data-testid="rollup-to-sheet">
              Comparison sheet
            </Link>
            <Link to="/money" className="text-ochre hover:text-white" data-testid="rollup-to-money">Capex, opex and returns</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
